import React, { useState, useEffect } from 'react';
import { gql } from '@apollo/client';
import { xor } from 'lodash-es';
import { Typography, Grid, Box, Paper } from 'vendor/material';
import {
  Banner,
  Button,
  Checkbox,
  Link,
  TextField,
} from '@tackle-io/platform-ui';
import { Field, Form, Formik } from 'formik';
import {
  useAzureVendorTackleAndAzureFormSectionQuery,
  useUpdateAzureVendorTackleAndAzureFormSectionMutation,
  AzureVendorTackleAndAzureFormSectionQuery,
  useAzureVendorTackleAndAzureFormSectionMspcConnectedLazyQuery,
} from 'generated/graphql';
import useStyles from '../../../../Settings/Account/marketplace/VendorConfig.styles';
import { FormProgress, FormProgressQueryResult } from 'utils/yup-extended';
import CoSellSettingsMicrosoftSolutions from 'pages/Settings/Account/components/CoSellSettings/CoSellSettingsMicrosoftSolutions';
import useAuthToken from 'hooks/useAuthToken';
import MsftCollapsibleFormSection from '../components/MsftCollapsibleFormSection';
import NoTogglePasswordField from '../components/NoTogglePasswordField';
import {
  CredentialValues,
  credentialSchema,
  MICROSOFT_PARTNER_APP_CLIENT_ID,
  MICROSOFT_PARTNER_CENTER_APP_CLIENT_ID,
  sendCredentialsToCloudConnector,
  userToCredentialValues,
  valuesToVendorConfigurationMsftUpdate,
} from '../utils/sharedSchemas';

type Values = CredentialValues;

// GraphQL queries and mutations (names contain Azure instead of Msft as is since they're defined in the backend)
export const AZURE_VENDOR_TACKLE_AND_AZURE_FORM_SECTION_QUERY = gql`
  query AzureVendorTackleAndAzureFormSection {
    currentUser {
      id
      vendor {
        id
        configuration {
          azure {
            exp_date
          }
        }
        onboarding {
          azure {
            account_setup
            commercial_marketplace
          }
        }
        coSell {
          id
          mspc_connected
          microsoftPartnerCenterConsentLink
          microsoftSolutions {
            id
          }
        }
      }
    }
  }
`;

export const AZURE_VENDOR_TACKLE_AND_AZURE_FORM_SECTION_MSPC_CONNECTED_QUERY = gql`
  query AzureVendorTackleAndAzureFormSectionMspcConnected {
    currentUser {
      id
      vendor {
        id
        coSell {
          id
          mspc_connected
        }
      }
    }
  }
`;

export const AZURE_VENDOR_TACKLE_AND_AZURE_FORM_SECTION_MUTATION = gql`
  mutation UpdateAzureVendorTackleAndAzureFormSection($updates: VendorInput!) {
    updateVendor(updates: $updates) {
      id
      configuration {
        azure {
          exp_date
        }
      }
      onboarding {
        azure {
          account_setup
          commercial_marketplace
        }
      }
      coSell {
        id
        mspc_connected
        microsoftPartnerCenterConsentLink
      }
    }
  }
`;

const calculateProgress = (
  isFromCoSell: boolean,
  data: AzureVendorTackleAndAzureFormSectionQuery,
): FormProgress => {
  let complete = 0;
  const account_setup =
    data?.currentUser?.vendor?.onboarding?.azure?.account_setup || [];

  if (account_setup.includes('tackle-access')) {
    complete = complete + 1;
  }
  if (!isFromCoSell && account_setup.includes('register-aad-app')) {
    complete = complete + 1;
  }
  if (data?.currentUser?.vendor?.configuration?.azure?.exp_date) {
    complete = complete + 1;
  }
  if (isFromCoSell && data?.currentUser?.vendor?.coSell?.mspc_connected) {
    complete = complete + 1;
  }
  if (
    isFromCoSell &&
    data?.currentUser?.vendor?.coSell?.microsoftSolutions?.length > 0
  ) {
    complete = complete + 1;
  }
  return {
    total: isFromCoSell ? 4 : 3,
    complete,
  };
};

const VendorTackleAndMsftFormSection = ({
  icon,
  isFromCoSell,
  onComplete,
}: {
  icon?: JSX.Element;
  isFromCoSell?: boolean;
  onComplete?: () => void;
}) => {
  const {
    data,
    loading: queryLoading,
    error,
  } = useAzureVendorTackleAndAzureFormSectionQuery({
    context: { batchKey: 'vendor-onboarding' },
  });
  const [updateValues, { loading: mutationLoading }] =
    useUpdateAzureVendorTackleAndAzureFormSectionMutation();

  const [isEditing, setIsEditing] = useState(false);

  const [mspcConsentLinkClicked, setMspcConsentLinkClicked] = useState(false);
  const [recheckMspcConnected, { loading: recheckMspcConnectedLoading }] =
    useAzureVendorTackleAndAzureFormSectionMspcConnectedLazyQuery({
      fetchPolicy: 'network-only',
    });

  const authToken = useAuthToken();

  useEffect(() => {
    if (mspcConsentLinkClicked) {
      const checkForMspcConnected = () => {
        recheckMspcConnected();
        setMspcConsentLinkClicked(false);
      };
      // recheck the status when they come back
      window.addEventListener('focus', checkForMspcConnected);
      return function cleanup() {
        window.removeEventListener('focus', checkForMspcConnected);
      };
    }
  }, [recheckMspcConnected, mspcConsentLinkClicked]);

  const loading =
    mutationLoading || queryLoading || recheckMspcConnectedLoading;
  const disabled = Boolean(loading || error || !data?.currentUser?.vendor);

  const classes = useStyles();

  const { complete, total } = calculateProgress(isFromCoSell, data);
  const percentComplete = (complete / total) * 100;

  const account_setup =
    data?.currentUser?.vendor?.onboarding?.azure?.account_setup || [];

  const hasMsftConfig =
    data?.currentUser?.vendor?.configuration?.azure?.exp_date;

  const hasNoMsftConfig = !hasMsftConfig;

  useEffect(() => {
    if (!queryLoading && hasNoMsftConfig) {
      setIsEditing(true);
    }
  }, [hasNoMsftConfig, queryLoading]);

  return (
    <>
      {isFromCoSell && (
        <Grid container spacing={2} direction="column">
          <Grid item>
            <Paper>
              <Box p={2}>
                <Grid container spacing={1} justifyContent="flex-end">
                  <Grid item>
                    <Link disableStyles to="/settings/account/co-sell">
                      <Button
                        appearance="secondary"
                        variant="outlined"
                        className={classes.buttonText}
                      >
                        Save draft and exit
                      </Button>
                    </Link>
                  </Grid>
                  <Grid item>
                    <Link
                      disableStyles
                      to="/settings/account/co-sell"
                      onClick={(e: React.SyntheticEvent<HTMLAnchorElement>) => {
                        if (percentComplete !== 100 || loading) {
                          e.preventDefault();
                        } else {
                          onComplete();
                        }
                      }}
                    >
                      <Button
                        appearance="primary"
                        className={classes.buttonText}
                        disabled={percentComplete !== 100 || loading}
                      >
                        Complete
                      </Button>
                    </Link>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
      <Formik<Values>
        enableReinitialize
        initialValues={userToCredentialValues(
          isEditing ? null : data?.currentUser,
        )}
        validationSchema={credentialSchema}
        onSubmit={() => {}}
      >
        {(formProps) => {
          const { values } = formProps;
          return (
            <MsftCollapsibleFormSection
              title={
                isFromCoSell
                  ? 'Set up co-sell for Microsoft'
                  : 'Connect Tackle and Microsoft'
              }
              subTitle={
                isFromCoSell
                  ? 'Give Tackle permissions to manage co-sell and provide support.'
                  : 'Give Tackle permission to manage listings and carry out marketplace transactions on your behalf.'
              }
              loading={loading}
              percentComplete={percentComplete}
              error={Boolean(error)}
              disabled={disabled}
              icon={icon}
              slug="connection-settings"
              timeToComplete="30 min"
              permissions="Global admin and Manager/Owner"
            >
              <Form>
                <Grid container direction="column">
                  {(error || (!queryLoading && !data?.currentUser?.vendor)) && (
                    <Grid item>
                      <Box p={3}>
                        <Banner
                          title="Unable to load data"
                          body="Something went wrong. Please refresh and try again or contact support."
                          type="danger"
                        />
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs>
                    <Box p={3} pb={2} className={classes.interGrid}>
                      <Grid container direction="column">
                        <Grid item>
                          <Checkbox
                            disabled={disabled}
                            className={classes.checkBox}
                            checked={account_setup.includes('tackle-access')}
                            label={
                              <Typography>
                                <b>User management</b>
                              </Typography>
                            }
                            onChange={() =>
                              updateValues({
                                variables: {
                                  updates: {
                                    id: data.currentUser.vendor.id,
                                    onboarding: {
                                      azure: {
                                        account_setup: xor(account_setup, [
                                          'tackle-access',
                                        ]),
                                      },
                                    },
                                  },
                                },
                              })
                            }
                          />
                        </Grid>
                        <Grid item className={classes.textGrid}>
                          <Box pl={4}>
                            <Typography>
                              Open{' '}
                              <Link
                                showExternalIcon
                                external
                                to="https://partner.microsoft.com/en-us/dashboard/account/v3/usermanagement#apps"
                                className={classes.link}
                              >
                                Partner Center User Management
                              </Link>{' '}
                              to see a list of existing users. If Tackle is not
                              already a user, select <b>Invite Users</b> at the
                              top of the list, invite Tackle using the email and
                              roles below, then select <b>Save</b>. If Tackle
                              already has access, amend the existing the role to
                              add <b>Referral Admin</b>. These roles grant
                              access to your referral program at Microsoft.
                            </Typography>
                            <Grid item xs className={classes.directions}>
                              <b>Email:</b> tackle-service-msft@tackle.io
                              <br />
                              <b>Role:</b> Referral Admin, Developer
                            </Grid>
                          </Box>
                        </Grid>
                      </Grid>
                      <Grid container direction="column">
                        <Grid item>
                          <Checkbox
                            disabled={disabled}
                            className={classes.checkBox}
                            label={
                              <Typography>
                                <b>Microsoft Active Directory</b>
                              </Typography>
                            }
                            checked={!hasNoMsftConfig && !isEditing}
                          />
                        </Grid>
                        <Grid item className={classes.textGrid}>
                          <Box pl={4}>
                            <ol className={classes.ol}>
                              <li>
                                <Typography>
                                  Open{' '}
                                  <Link
                                    showExternalIcon
                                    external
                                    to="http://portal.azure.com/#blade/Microsoft_AAD_IAM/ActiveDirectoryMenuBlade/RegisteredApps"
                                    className={classes.link}
                                  >
                                    Active Directory
                                  </Link>{' '}
                                  .{' '}
                                  <b>
                                    If Tackle is not already set up with an app:
                                  </b>{' '}
                                  Select <b>+New Registration</b>, and then
                                  enter the "Tackle Marketplace App" information
                                  below and select Register.
                                </Typography>
                                <Box mt={2}>
                                  <Typography>
                                    <b>
                                      If Active Directory is already set up for
                                      Tackle:
                                    </b>{' '}
                                    Select it from the list of existing apps,
                                    and amend the <b>Redirect URL</b> to include
                                    the one below.
                                  </Typography>
                                </Box>
                                <Grid item xs className={classes.directions}>
                                  <b>Name:</b> Tackle Marketplace App
                                  <br />
                                  <b>Supported Account Types:</b> Any Microsoft
                                  AD directory - Multi-tenant
                                  <br />
                                  <b>Platform:</b> Web
                                  <br />
                                  <b>Redirect URI (required):</b>{' '}
                                  https://co-sell-api.tackle.io/api/v2/vendor/msft-partner-consent
                                </Grid>
                              </li>
                              <li>
                                <Typography>
                                  On the <b>Overview</b> page, copy the{' '}
                                  <b>Application (client) ID</b> and{' '}
                                  <b>Directory (tenant) ID</b> values, record
                                  them here, and select <b>Save</b>.
                                </Typography>
                                <Grid
                                  item
                                  xs={12}
                                  className={classes.marginTopBump}
                                >
                                  <Field name="applicationId">
                                    {({ field, meta }) => (
                                      <NoTogglePasswordField
                                        {...field}
                                        id="applicationId"
                                        disabled={disabled || !isEditing}
                                        error={meta.touched && meta.error}
                                        label="Application (client) ID *"
                                        value={
                                          !isEditing && !queryLoading
                                            ? '*************************'
                                            : field.value
                                        }
                                      />
                                    )}
                                  </Field>
                                </Grid>
                                <Grid
                                  item
                                  xs={12}
                                  className={classes.marginTopBump}
                                >
                                  <Field name="tenantId">
                                    {({ field, meta }) => (
                                      <NoTogglePasswordField
                                        {...field}
                                        id="tenantId"
                                        disabled={disabled || !isEditing}
                                        error={meta.touched && meta.error}
                                        label="Directory (tenant) ID *"
                                        value={
                                          !isEditing && !queryLoading
                                            ? '*************************'
                                            : field.value
                                        }
                                      />
                                    )}
                                  </Field>
                                </Grid>
                              </li>
                              <li>
                                <Typography>
                                  In the left navigation, select{' '}
                                  <b>Certificates & secrets</b>. Select{' '}
                                  <b>+New</b> client secret.{' '}
                                </Typography>
                              </li>
                              <li>
                                <Typography>
                                  Add a description (like, "Tackle Marketplace
                                  Secret") and set the expiration to 24 months.
                                </Typography>
                              </li>
                              <li>
                                <Typography>
                                  Copy and paste the new <b>secret value</b> (
                                  not the secret ID) and the expiration date
                                  here, and select <b>Save</b>.
                                </Typography>
                                <Grid
                                  container
                                  className={classes.marginTopBump}
                                  spacing={2}
                                >
                                  <Grid item xs={12} md={6}>
                                    <Field name="applicationSecretKeyValue">
                                      {({ field, meta }) => (
                                        <NoTogglePasswordField
                                          {...field}
                                          id="applicationSecretKeyValue"
                                          disabled={disabled || !isEditing}
                                          error={meta.touched && meta.error}
                                          label="Application Secret Key Value *"
                                          value={
                                            !isEditing && !queryLoading
                                              ? '***********************************'
                                              : field.value
                                          }
                                        />
                                      )}
                                    </Field>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Field name="applicationSecretKeyExpirationDate">
                                      {({ field, meta }) => (
                                        <TextField
                                          {...field}
                                          id="applicationSecretKeyExpirationDate"
                                          disabled={disabled || !isEditing}
                                          error={meta.touched && meta.error}
                                          label="Application Secret Key Expiration Date *"
                                          type="date"
                                        />
                                      )}
                                    </Field>
                                  </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                  <Grid
                                    container
                                    spacing={2}
                                    justifyContent="space-between"
                                  >
                                    <Grid item>
                                      {!hasNoMsftConfig && (
                                        <Button
                                          id={`msftVendorTackleAndMsftFormButton_${
                                            isEditing ? 'cancel' : 'edit'
                                          }`}
                                          name={isEditing ? 'cancel' : 'edit'}
                                          appearance="primary"
                                          onClick={() => {
                                            formProps.resetForm();
                                            setIsEditing(!isEditing);
                                          }}
                                          variant="text"
                                        >
                                          {isEditing ? 'Cancel' : 'Edit'}
                                        </Button>
                                      )}
                                    </Grid>
                                    <Grid item>
                                      <Button
                                        className={classes.buttonText}
                                        id="msftVendorTackleAndMsftFormButton_save"
                                        name="save"
                                        appearance="primary"
                                        disabled={
                                          disabled ||
                                          !formProps.dirty ||
                                          !formProps.isValid
                                        }
                                        loading={
                                          formProps.isSubmitting || loading
                                        }
                                        type="submit"
                                        onClick={async () => {
                                          try {
                                            const upstreamResult =
                                              await updateValues({
                                                variables: {
                                                  updates: {
                                                    id: data.currentUser?.vendor
                                                      ?.id,
                                                    configuration: {
                                                      azure:
                                                        valuesToVendorConfigurationMsftUpdate(
                                                          values,
                                                        ),
                                                    },
                                                  },
                                                },
                                              });

                                            if (upstreamResult) {
                                              try {
                                                await sendCredentialsToCloudConnector(
                                                  values,
                                                  authToken,
                                                );
                                                setIsEditing(false);
                                              } catch (error) {
                                                console.error(
                                                  'Failed to update credentials in cloud-connector-msft',
                                                  error,
                                                );
                                              }
                                            }
                                          } catch (error) {
                                            console.error(
                                              'Failed to update credentials in upstream',
                                              error,
                                            );
                                          }
                                        }}
                                      >
                                        Save
                                      </Button>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </li>
                            </ol>
                          </Box>
                        </Grid>
                      </Grid>
                      {isFromCoSell && (
                        <Grid container direction="column">
                          <Grid item>
                            <Checkbox
                              disabled={disabled}
                              className={classes.checkBox}
                              label={
                                <Typography>
                                  <b>API permissions</b>
                                </Typography>
                              }
                              checked={
                                data?.currentUser?.vendor?.coSell
                                  ?.mspc_connected
                              }
                            />
                          </Grid>
                          <Grid item className={classes.textGrid}>
                            <Box pl={4}>
                              <Typography>
                                Specific API permissions are required in order
                                to allow the Tackle Marketplace App to access
                                the Partner Center API and Referrals so you can
                                reduce the need to enter into Partner Center
                                directly to facilitate co-selling motions.
                              </Typography>
                              <ol className={classes.ol}>
                                <Typography>
                                  <li>
                                    In the{' '}
                                    <Link
                                      showExternalIcon
                                      external
                                      to="https://portal.azure.com/#home"
                                      className={classes.link}
                                    >
                                      Active Directory
                                    </Link>
                                    , click the Tackle Marketplace App you
                                    created, then click <b>API Permissions</b>.
                                  </li>
                                  <li>
                                    Select <b>Add permission</b> and then{' '}
                                    <b>APIs My Organization Uses</b>.
                                  </li>
                                  <li>
                                    Add the <b>User impersonation</b> API
                                    Permissions for the:
                                    <ol>
                                      <li
                                        className={
                                          classes.listStyleTypeAsLowerAlpha
                                        }
                                      >
                                        <i>Microsoft Partner</i> permission with
                                        the Application (client) ID of{' '}
                                        <i>{MICROSOFT_PARTNER_APP_CLIENT_ID}</i>
                                      </li>
                                      <li
                                        className={
                                          classes.listStyleTypeAsLowerAlpha
                                        }
                                      >
                                        <i>Microsoft Partner Center</i>{' '}
                                        permission with the Application (client)
                                        ID of{' '}
                                        <i>
                                          {
                                            MICROSOFT_PARTNER_CENTER_APP_CLIENT_ID
                                          }
                                        </i>
                                      </li>
                                    </ol>
                                  </li>
                                  <li>
                                    These permissions will show in Active
                                    Directory as permissions not granted.
                                    Request <b>Admin Consent</b> to grant Tackle
                                    API permissions.
                                  </li>

                                  <Box pt={2} pb={2}>
                                    <Banner
                                      title="You must have referral admin permission in Partner Center* for the authorization to work properly."
                                      body="*Make sure you are signed into the correct Partner Center environment if you have multiple environments. If you were assigned referral admin permissions within the last 24 hours, please wait 24 hours before they take effect."
                                      type="warning"
                                      borderPosition="top"
                                      isCollapsible={true}
                                      defaultOpen={false}
                                    />
                                  </Box>
                                  <li>
                                    {data?.currentUser?.vendor?.coSell
                                      ?.microsoftPartnerCenterConsentLink ? (
                                      <span>
                                        Click the link below to allow Tackle to
                                        use the Partner Center Referral API to
                                        submit co-sell opportunities on your
                                        behalf and notify you of updates.{' '}
                                        <Link
                                          external
                                          showExternalIcon
                                          onClick={() =>
                                            setMspcConsentLinkClicked(true)
                                          }
                                          to={
                                            data?.currentUser?.vendor?.coSell
                                              ?.microsoftPartnerCenterConsentLink
                                          }
                                          className={classes.link}
                                        >
                                          Go to Microsoft Partner Center
                                          Permissions
                                        </Link>{' '}
                                      </span>
                                    ) : (
                                      <span>
                                        Complete the previous steps to reveal
                                        this step
                                      </span>
                                    )}
                                  </li>
                                  {!data?.currentUser?.vendor?.coSell
                                    ?.mspc_connected && (
                                    <li>
                                      Once the above steps have been completed
                                      the checkbox next to the header{' '}
                                      <strong>API permissions</strong> should be
                                      marked as complete. If not{' '}
                                      <Link
                                        to="#"
                                        onClick={(
                                          e: React.MouseEvent<HTMLElement>,
                                        ) => {
                                          e.preventDefault();
                                          recheckMspcConnected();
                                        }}
                                      >
                                        click here
                                      </Link>{' '}
                                      to recheck the permissions or try again.
                                    </li>
                                  )}
                                </Typography>
                              </ol>
                            </Box>
                          </Grid>
                        </Grid>
                      )}
                      {isFromCoSell && <CoSellSettingsMicrosoftSolutions />}
                      {!isFromCoSell && (
                        <Grid container direction="column">
                          <Grid item>
                            <Checkbox
                              disabled={disabled}
                              className={classes.checkBox}
                              label={
                                <Typography>
                                  <b>User management apps</b>
                                </Typography>
                              }
                              checked={account_setup.includes(
                                'register-aad-app',
                              )}
                              onChange={() =>
                                updateValues({
                                  variables: {
                                    updates: {
                                      id: data.currentUser.vendor.id,
                                      onboarding: {
                                        azure: {
                                          account_setup: xor(account_setup, [
                                            'register-aad-app',
                                          ]),
                                        },
                                      },
                                    },
                                  },
                                })
                              }
                            />
                          </Grid>
                          <Grid item className={classes.textGrid}>
                            <Box pl={4}>
                              <Typography>
                                (Open the{' '}
                                <Link
                                  showExternalIcon
                                  external
                                  to="https://partner.microsoft.com/en-us/dashboard/account/v3/usermanagement#apps"
                                  className={classes.link}
                                >
                                  Partner Center User Management Apps
                                </Link>{' '}
                                . Be sure to find the correct directory.)
                              </Typography>
                              <ol className={classes.ol}>
                                <Typography>
                                  <li>
                                    Select <b>+Add Microsoft AD Application</b>.
                                  </li>
                                  <li>
                                    Find and select the application you just
                                    created: "Tackle Marketplace App", then
                                    select <b>Next</b>.
                                  </li>
                                  <li>
                                    Select the <b>Manager (Windows)</b> checkbox
                                    and select <b>Add</b>. Verify that it was
                                    added successfully.
                                  </li>
                                </Typography>
                              </ol>
                            </Box>
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Form>
            </MsftCollapsibleFormSection>
          );
        }}
      </Formik>
    </>
  );
};

export const useVendorTackleAndMsftFormSectionProgress = (
  isFromCoSell: boolean,
): FormProgressQueryResult => {
  const { data, loading, error } = useAzureVendorTackleAndAzureFormSectionQuery(
    {
      context: { batchKey: 'vendor-onboarding' },
    },
  );
  const progress = calculateProgress(isFromCoSell, data);
  return { progress, loading, didError: Boolean(error) };
};

export default VendorTackleAndMsftFormSection;
